import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-2xl border border-border/50 bg-card text-card-foreground shadow-sm transition-all duration-300",
  {
    variants: {
      hover: {
        none: "",
        lift: "hover:-translate-y-1 hover:shadow-lg hover:shadow-foreground/5",
        glow: "hover:border-primary/40 hover:shadow-[0_0_20px_rgba(193,255,114,0.15)]",
        glass: [
          "backdrop-blur-sm bg-card/80",
          "hover:bg-card/90",
          "hover:shadow-[0_8px_32px_rgba(193,255,114,0.08),0_4px_16px_rgba(4,211,188,0.06)]",
          "hover:border-border/70",
        ],
      },
      depth: {
        none: "",
        subtle: "shadow-brand-sm",
        medium: "shadow-brand-md",
        large: "shadow-brand-lg",
      },
      gradient: {
        none: "",
        subtle: "relative before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-br before:from-primary/5 before:via-transparent before:to-accent/5 before:pointer-events-none before:z-0 [&>*]:relative [&>*]:z-10",
      },
    },
    defaultVariants: {
      hover: "none",
      depth: "none",
      gradient: "none",
    },
  }
)

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>
>(({ className, hover, depth, gradient, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ hover, depth, gradient }), className)}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export type CardVariants = VariantProps<typeof cardVariants>

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
